import React, { useContext, useMemo } from "react";
import clsx from "clsx";

import { LESSON5_ASSESSMENT_AXIS } from "../../../../constants/active_learning";
import { Context } from "../../../../teacherStore";
import { getStudentAnswersByQuestion } from "../../../../libs/ActiveLearning";
import TreeCard from "../../../../atoms/ActiveLearning/TreeCard";
import styles from "../ActiveLearningLessonAnswerData.module.scss";

interface ILesson5Pros {
  materialCode: string;
  lessonNo: number;
}

export const Lesson5: React.FC<ILesson5Pros> = ({ materialCode, lessonNo }) => {
  const { state } = useContext(Context);
  const activeLearningStudentAnswers = state.active_learning_student_answers;

  const answersL4I3Q1 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningStudentAnswers,
      materialCode === "ECS001" ? 4 : 5,
      3,
      1,
    );

    return answers.length > 0
      ? answers.map(answer => answer.contents.value).join("")
      : "回答データがありません。";
  }, [materialCode, activeLearningStudentAnswers]);

  const answersL5I1Q1 = useMemo(() => {
    return getStudentAnswersByQuestion(
      materialCode,
      activeLearningStudentAnswers,
      lessonNo,
      1,
      1,
    );
  }, [materialCode, activeLearningStudentAnswers, lessonNo]);

  const answersL5I2 = useMemo(() => {
    const answersQ1 = getStudentAnswersByQuestion(
      materialCode,
      activeLearningStudentAnswers,
      lessonNo,
      2,
      1,
    );
    const answersQ2 = getStudentAnswersByQuestion(
      materialCode,
      activeLearningStudentAnswers,
      lessonNo,
      2,
      2,
    );
    const answersQ3 = getStudentAnswersByQuestion(
      materialCode,
      activeLearningStudentAnswers,
      lessonNo,
      2,
      3,
    );
    const answersQ4 = getStudentAnswersByQuestion(
      materialCode,
      activeLearningStudentAnswers,
      lessonNo,
      2,
      4,
    );
    const answersQ5 = getStudentAnswersByQuestion(
      materialCode,
      activeLearningStudentAnswers,
      lessonNo,
      2,
      5,
    );

    const allAnswers = [
      ...answersQ1,
      ...answersQ2,
      ...answersQ3,
      ...answersQ4,
      ...answersQ5,
    ];

    const result = answersL5I1Q1.map(answer => {
      const uidMatches = allAnswers.filter(
        q => q.contents.parentUid === answer.contents.uid,
      );
      return [...uidMatches, answer];
    });

    return result;
  }, [materialCode, activeLearningStudentAnswers, answersL5I1Q1, lessonNo]);

  const answersL5I2Q6 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningStudentAnswers,
      lessonNo,
      2,
      6,
    );

    return answers.length > 0
      ? answers.map(answer => answer.contents.value).join("")
      : "回答データがありません。";
  }, [materialCode, activeLearningStudentAnswers, lessonNo]);

  const answersL5I2Q7 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningStudentAnswers,
      lessonNo,
      2,
      7,
    );

    return answers.length > 0
      ? answers.map(answer => answer.contents.value).join("")
      : "回答データがありません。";
  }, [materialCode, activeLearningStudentAnswers, lessonNo]);

  const answersL5I3Q1 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningStudentAnswers,
      lessonNo,
      3,
      1,
    );

    return answers.length > 0
      ? answers.map(answer => answer.contents.value).join("")
      : "回答データがありません。";
  }, [materialCode, activeLearningStudentAnswers, lessonNo]);

  return (
    <div className={styles.lesson}>
      <p className={styles.title}>
        Lesson{lessonNo < 10 ? `0${lessonNo}` : lessonNo}{" "}
        「課題」に挑み、解決のアイデアを見つけよう
      </p>
      <div className={styles.inner}>
        <div className={styles.work}>
          <p className={styles.no}>WORK01</p>
          <div className={styles.question}>
            <p className={styles.heading}>
              「課題」を解決するアイデアを考えてみよう。
            </p>
            <div className={styles.tree}>
              <TreeCard title={"課題"} text={answersL4I3Q1} />
              <ul className={styles.branchList}>
                {answersL5I1Q1.length > 0 ? (
                  answersL5I1Q1.map(answer => (
                    <li key={answer.id} className={styles.branch}>
                      <div className={styles.labelWrapper}>
                        <span className={styles.label}>解決策</span>
                      </div>
                      <p className={styles.answer}>{answer.contents.value}</p>
                    </li>
                  ))
                ) : (
                  <li className={styles.branch}>
                    <div className={styles.labelWrapper}>
                      <span className={styles.label}>解決策</span>
                    </div>
                    <p className={styles.answer}>回答データがありません。</p>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
        <div className={styles.work}>
          <p className={styles.no}>WORK02</p>
          {answersL5I2.length > 0 ? (
            answersL5I2.map((answers, index) => (
              <div key={index} className={styles.question}>
                {index === 0 && (
                  <p className={styles.heading}>
                    実現したいと思う解決のアイデアを決めよう。
                  </p>
                )}
                <div key={index} className={styles.tree}>
                  <p className={styles.answer}>
                    {answers.map(
                      answer =>
                        answer.question_no === 1 && answer.contents?.value,
                    )}
                  </p>
                  <ul className={styles.branchList}>
                    <li className={styles.branch}>
                      <div className={styles.labelWrapper}>
                        <span className={styles.label}>評価軸</span>
                      </div>
                      <ul className={clsx(styles.answer, styles.list)}>
                        {answers.map(answer => {
                          if (
                            answer.issue_no === 2 &&
                            answer.question_no >= 1 &&
                            answer.question_no <= 4
                          ) {
                            return (
                              <li key={answer.id} className={styles.item}>
                                {LESSON5_ASSESSMENT_AXIS[answer.question_no]}→
                                {answer.contents?.parentLabel}
                              </li>
                            );
                          }
                          return null;
                        })}
                      </ul>
                    </li>
                    <li className={styles.branch}>
                      <div className={styles.labelWrapper}>
                        <span className={styles.label}>調べたこと</span>
                      </div>
                      <p className={clsx(styles.answer)}>
                        {answers.map(
                          answer =>
                            answer.question_no === 5 && answer.contents?.value,
                        )}
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            ))
          ) : (
            <div className={styles.question}>
              <p className={styles.heading}>
                実現したいと思う解決のアイデアを決めよう。
              </p>
              <div className={styles.tree}>
                <p className={styles.answer}>回答データがありません。</p>
                <ul className={styles.branchList}>
                  <li className={styles.branch}>
                    <div className={styles.labelWrapper}>
                      <span className={styles.label}>評価軸</span>
                    </div>
                    <ul className={clsx(styles.answer, styles.list)}>
                      回答データがありません。
                    </ul>
                  </li>
                  <li className={styles.branch}>
                    <div className={styles.labelWrapper}>
                      <span className={styles.label}>調べたこと</span>
                    </div>
                    <p className={clsx(styles.answer)}>
                      回答データがありません。
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          )}
          <div className={styles.question}>
            <p className={clsx(styles.heading, styles.dot)}>
              実現したいと思う解決のアイデアを選ぼう。
            </p>
            <p className={styles.answer}>{answersL5I2Q6}</p>
          </div>
          <div className={styles.question}>
            <p className={clsx(styles.heading, styles.dot)}>
              解決のアイデアを決めるまでに考えたことを入力しよう。
            </p>
            <p className={clsx(styles.answer, styles.high)}>{answersL5I2Q7}</p>
          </div>
        </div>
        <div className={styles.work}>
          <p className={styles.no}>WORK03</p>
          <div className={styles.question}>
            <p className={styles.heading}>
              解決のアイデアの中身を具体的に考えよう。
            </p>
            <p className={clsx(styles.answer, styles.high)}>{answersL5I3Q1}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Lesson5;
